import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Icon } from '../../components/Icon'
import { Call112Button, EmergencyHelpShell } from './chrome'
import { useArrivalFocus } from './focus'
import { hospitalsPath } from './paths'

/**
 * Emergency Help, screen 1: get emergency help (M35-T06, spec §C.1.1,
 * ADR-0064). At `/emergency` since M36's switch-over (M36-T11).
 *
 * 112 first, and nothing in its way: this screen loads no data, asks for no
 * location and has no form, so the call button is usable the moment it paints.
 * There is no symptom grid (the spec forbids automatic triage) and no link to
 * Medcor Transport, which cannot be used in an emergency (ADR-0063).
 */
export function EmergencyStart(): ReactNode {
  const heading = useArrivalFocus<HTMLHeadingElement>()
  return (
    <EmergencyHelpShell step={1} title="Get emergency help">
      <section
        aria-labelledby="eh-life"
        className="flex flex-col gap-3 rounded-r3 border-[1.5px] border-sos bg-sos-bg px-4 py-4"
      >
        <h1
          id="eh-life"
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-sos focus:outline-none"
        >
          Life-threatening emergency?
        </h1>
        <p className="text-[13px] leading-relaxed text-ink-1">
          Call Ghana emergency services now. Do not wait to finish any Medcor
          forms.
        </p>
        <Call112Button />
        {/* Owner ruling Q8 / ADR-0063's approved exception, word for word. */}
        <p className="text-[12.5px] font-medium text-sos">
          Medcor does not dispatch ambulances.
        </p>
        {/* Plain text, deliberately not a link (owner Decision 2). */}
        <p className="text-[12px] text-ink-2">
          Medcor Transport cannot be used in an emergency.
        </p>
      </section>

      <section aria-labelledby="eh-find" className="flex flex-col gap-2">
        <h2 id="eh-find" className="text-[15px] font-semibold text-ink-1">
          Find a partner hospital
        </h2>
        <p className="text-[13px] leading-relaxed text-ink-2">
          See an AMTC partner hospital&apos;s emergency contact details and how
          to get there.
        </p>
        <Link
          to={hospitalsPath}
          className="flex items-center justify-center gap-2 rounded-r3 bg-ink-1 px-4 py-3 text-[14px] font-semibold text-bg-app focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Find an AMTC partner hospital <Icon name="arrow" size={15} />
        </Link>
      </section>

      <section
        aria-labelledby="eh-already"
        className="rounded-r3 border border-line-1 bg-bg-soft px-4 py-3"
      >
        <h2 id="eh-already" className="text-[14px] font-semibold text-ink-1">
          Already speaking to 112?
        </h2>
        <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
          Follow the dispatcher&apos;s advice. You may browse hospital details
          without changing the emergency plan.
        </p>
      </section>

      <p className="flex items-start gap-2 text-[12px] leading-relaxed text-ink-3">
        <Icon name="info" size={14} className="mt-0.5 shrink-0" />
        Only 10 AMTC partners are listed. Other hospitals may be more
        appropriate in an emergency.
      </p>
    </EmergencyHelpShell>
  )
}
